import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AuthState } from '@/types/auth';

interface AuthContextValue {
  auth: AuthState;
  login: (token: string, expiresAt: string) => void;
  logout: () => void;
}

const initialAuthState: AuthState = {
  isAuthenticated: false,
  sessionToken: null,
  expiresAt: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(initialAuthState);

  const login = useCallback((token: string, expiresAt: string) => {
    setAuth({
      isAuthenticated: true,
      sessionToken: token,
      expiresAt,
    });
  }, []);

  const logout = useCallback(() => {
    setAuth((prev) => ({
      ...prev,
      isAuthenticated: false,
      sessionToken: null,
      expiresAt: null,
    }));
  }, []);

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
